export type OverlayAttribute =
  | 'data-layer'
  | 'data-layer-sub'
  | 'data-layer-variant'

interface SvgOverlayIndex {
  byAttribute: Record<OverlayAttribute, Map<string, SVGElement[]>>
  variants: SVGElement[]
}

const overlayIndexCache = new WeakMap<SVGSVGElement, SvgOverlayIndex>()

const ATTRIBUTES: OverlayAttribute[] = [
  'data-layer',
  'data-layer-sub',
  'data-layer-variant',
]

const emptyIndex = (): SvgOverlayIndex => ({
  byAttribute: {
    'data-layer': new Map(),
    'data-layer-sub': new Map(),
    'data-layer-variant': new Map(),
  },
  variants: [],
})

const pushElement = (
  groups: Map<string, SVGElement[]>,
  key: string,
  element: SVGElement,
): void => {
  const existing = groups.get(key)
  if (existing) {
    existing.push(element)
    return
  }
  groups.set(key, [element])
}

/** Walk layer nodes once per SVG element. Later lookups reuse this map. */
export const getSvgOverlayIndex = (svg: SVGSVGElement): SvgOverlayIndex => {
  const cached = overlayIndexCache.get(svg)
  if (cached) {
    return cached
  }
  const index = emptyIndex()
  svg
    .querySelectorAll<SVGElement>(
      '[data-layer], [data-layer-sub], [data-layer-variant]',
    )
    .forEach((element) => {
      ATTRIBUTES.forEach((attribute) => {
        const key = element.getAttribute(attribute)
        if (!key) {
          return
        }
        pushElement(index.byAttribute[attribute], key, element)
        if (attribute === 'data-layer-variant') {
          index.variants.push(element)
        }
      })
    })
  overlayIndexCache.set(svg, index)
  return index
}

const resolveSvg = (node: Element): SVGSVGElement | null => {
  if (node instanceof SVGSVGElement) {
    return node
  }
  if (node instanceof SVGElement) {
    return node.ownerSVGElement
  }
  return null
}

export const getLayerElements = (
  svg: SVGSVGElement,
  attribute: OverlayAttribute,
  key: string,
): SVGElement[] => getSvgOverlayIndex(svg).byAttribute[attribute].get(key) ?? []

export const getFirstLayerElement = (
  svg: SVGSVGElement,
  attribute: OverlayAttribute,
  key: string,
): SVGElement | null => getLayerElements(svg, attribute, key)[0] ?? null

/** Variants that live inside `root`, from the SVG index. */
export const getVariantElementsWithin = (root: Element): SVGElement[] => {
  const svg = resolveSvg(root)
  if (!svg) {
    return Array.from(root.querySelectorAll<SVGElement>('[data-layer-variant]'))
  }
  const variants = getSvgOverlayIndex(svg).variants
  if (root === svg) {
    return variants
  }
  return variants.filter(
    (element) => element !== root && root.contains(element),
  )
}
